import { z } from "zod";
import { CitySchema } from "../cities";

export const ExpertProfileMediaSchema = z.object({
    id: z.string(),
    cdn_url: z.string(),
    mime_type: z.string().optional(),
    size: z.number().optional(),
});

export type ExpertProfileMedia = z.infer<typeof ExpertProfileMediaSchema>;

export const ExpertProfileUserSchema = z.object({
    id: z.number().int().positive(),
    email: z.email(),
    email_verified_at: z.string().nullable(),
});

export type ExpertProfileUser = z.infer<typeof ExpertProfileUserSchema>;

export const ExpertProfileDetailsSchema = z.object({
    slug: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    city: CitySchema.nullable().optional(),
    starting_price: z.number().nullable().optional(),
    timezone: z.string().nullable().optional(),
    phone_no: z.string().nullable().optional(),
    regions_served: z.array(z.string()).default([]),
    geo_regions: z.array(z.string()).default([]),
    languages: z.array(z.string()).default([]),
    partner_tier: z.string().nullable().optional(),
    profile_picture: z.string().nullable().optional(),
    profile_picture_media: ExpertProfileMediaSchema.nullable().optional(),
});

export type ExpertProfileDetails = z.infer<typeof ExpertProfileDetailsSchema>;

export const ExpertFreelancerSchema = z.object({
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    role: z.string().nullable().optional(),
});

export type ExpertFreelancer = z.infer<typeof ExpertFreelancerSchema>;

export const ExpertAgencySchema = z.object({
    agency_name: z.string().nullable().optional(),
    agency_type: z.string().nullable().optional(),
    agency_type_fields: z
        .object({
            app_tier: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    representative_name: z.string().nullable().optional(),
    representative_role: z.string().nullable().optional(),
    representative_picture: z.string().nullable().optional(),
    representative_picture_media: ExpertProfileMediaSchema.nullable().optional(),
});

export type ExpertAgency = z.infer<typeof ExpertAgencySchema>;

export const ExpertSocialLinksSchema = z.object({
    linkedin_url: z.string().nullable().optional(),
    github_url: z.string().nullable().optional(),
    x_url: z.string().nullable().optional(),
    fiverr_url: z.string().nullable().optional(),
    upwork_url: z.string().nullable().optional(),
    toptal_url: z.string().nullable().optional(),
    storetasker_url: z.string().nullable().optional(),
    other_listing_url: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    shopify_directory_link: z.string().nullable().optional(),
    booking_call_url: z.string().nullable().optional(),
    google_merchant: z.string().nullable().optional(),
});

export type ExpertSocialLinks = z.infer<typeof ExpertSocialLinksSchema>;

export const ExpertAddressSchema = z.object({
    address: z.string().nullable().optional(),
    longitude: z.number().nullable().optional(),
    latitude: z.number().nullable().optional(),
});

export type ExpertAddress = z.infer<typeof ExpertAddressSchema>;

export const ExpertPendingEmailChangeSchema = z.object({
    new_email: z.email(),
    requested_at: z.string(),
    expires_at: z.string(),
});

export type ExpertPendingEmailChange = z.infer<typeof ExpertPendingEmailChangeSchema>;

export const ExpertProfilePlanSummarySchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    slug: z.string(),
    billing_cycle: z.string(),
    price_cents: z.number().int().nonnegative(),
    currency: z.string(),
    is_founding: z.boolean(),
    is_active: z.boolean(),
});

export type ExpertProfilePlanSummary = z.infer<typeof ExpertProfilePlanSummarySchema>;

export const ExpertProfileSubscriptionSummarySchema = z.object({
    id: z.number().int().positive(),
    status: z.string(),
    starts_at: z.string().nullable().optional(),
    ends_at: z.string().nullable().optional(),
    renews_at: z.string().nullable().optional(),
    cancelled_at: z.string().nullable().optional(),
    invoices_count: z.number().int().nonnegative(),
    plan: ExpertProfilePlanSummarySchema,
});

export type ExpertProfileSubscriptionSummary = z.infer<typeof ExpertProfileSubscriptionSummarySchema>;

export const ExpertProfileSchema = z.object({
    user: ExpertProfileUserSchema,
    user_type: z.enum(["freelancer", "agency"]),
    expert: ExpertProfileDetailsSchema,
    freelancer: ExpertFreelancerSchema.nullable(),
    agency: ExpertAgencySchema.nullable(),
    social_links: ExpertSocialLinksSchema.nullable(),
    address: ExpertAddressSchema.nullable(),
    pending_email_change: ExpertPendingEmailChangeSchema.nullable(),
    subscription_summary: ExpertProfileSubscriptionSummarySchema.nullable(),
});

export type ExpertProfile = z.infer<typeof ExpertProfileSchema>;
